import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Modal, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faEllipsisV, faTimesCircle, faBars } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import BackButton from '../../../../Components/BackButton';
import { DashboardLayout } from '../../../../Layout/DashboardLayout';
import CustomButton from '../../../../Components/CustomButton';
import { getRequest, patchRequest, postRequest } from '../../../../Util/requests';
import { errorModal, questionModal, successModal } from '../../../../Components/CustomModal';
import CustomLoader from '../../../../Components/CustomLoader';
import CustomTable from '../../../../Components/CustomTable';
import { placeholderImage } from '../../../../Assets/images';
import { AddImageComponent, EditImageComponent } from '../../../CategoryManagement/ImageComponent';

const IndustryParentAddEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [paragrah, setParagrah] = useState("");
    const [items, setItems] = useState([]);
    const [loader, setLoader] = useState(false);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [currentItem, setCurrentItem] = useState({
        title: "",
        subtitle: "",
        paragraph: "",
        sequence: "",
        image: null,
        imagePreview: null,
        banner: null,
        bannerPreview: null,
        acronyms: []
    });
    const [newAcronym, setNewAcronym] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoader(true);
                const response = await getRequest(`/api/user/industry-parents/${id}/`);
                const data = response.data.data ? response.data.data[0] : response.data;
                if (data) {
                    setParagrah(data.paragrah || data.paragraph || "");
                    const formattedItems = (data.array || []).map(item => ({
                        ...item,
                        imagePreview: item.image || null,
                        bannerPreview: item.banner || null,
                        acronyms: Array.isArray(item.acronyms) ? item.acronyms : (item.acroynms || [])
                    }));
                    setItems(formattedItems);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoader(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id]);

    const handleOpenModal = (index = null) => {
        if (index !== null) {
            setEditingIndex(index);
            setCurrentItem({
                ...items[index],
                imagePreview: items[index].imagePreview || items[index].image,
                bannerPreview: items[index].bannerPreview || items[index].banner
            });
        } else {
            setEditingIndex(null);
            setCurrentItem({
                title: "",
                subtitle: "",
                paragraph: "",
                sequence: items.length + 1,
                image: null,
                imagePreview: null,
                banner: null,
                bannerPreview: null,
                acronyms: []
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingIndex(null);
        setCurrentItem({
            title: "",
            subtitle: "",
            paragraph: "",
            sequence: "",
            image: null,
            imagePreview: null,
            banner: null,
            bannerPreview: null,
            acronyms: []
        });
        setNewAcronym("");
    };

    const handleImageChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'image') {
                    setCurrentItem({ ...currentItem, image: file, imagePreview: reader.result });
                } else {
                    setCurrentItem({ ...currentItem, banner: file, bannerPreview: reader.result });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (type) => {
        if (type === 'image') {
            setCurrentItem({ ...currentItem, image: null, imagePreview: null });
        } else {
            setCurrentItem({ ...currentItem, banner: null, bannerPreview: null });
        }
    };

    const handleAddAcronym = () => {
        if (newAcronym.trim()) {
            setCurrentItem({
                ...currentItem,
                acronyms: [...currentItem.acronyms, newAcronym.trim()]
            });
            setNewAcronym("");
        }
    };

    const handleRemoveAcronym = (index) => {
        const updatedAcronyms = currentItem.acronyms.filter((_, i) => i !== index);
        setCurrentItem({ ...currentItem, acronyms: updatedAcronyms });
    };

    const handleSaveItem = () => {
        if (!currentItem.title || !currentItem.paragraph) {
            alert("Title and Paragraph are required.");
            return;
        }

        if (editingIndex !== null) {
            const newItems = [...items];
            newItems[editingIndex] = currentItem;
            setItems(newItems);
        } else {
            setItems([...items, currentItem]);
        }
        handleCloseModal();
    };

    const handleRemoveItem = (index) => {
        questionModal.fire({
            title: "Are you sure you want to remove this item?",
            confirmButtonText: "Remove",
        }).then((result) => {
            if (result.isConfirmed) {
                const newItems = items.filter((_, i) => i !== index);
                setItems(newItems);
            }
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!paragrah) return;

        const result = await questionModal.fire({
            title: `Do you want to ${id ? "Update" : "Add"} this Buying Industry and its Items?`,
            confirmButtonText: id ? "Update" : "Add",
        });

        if (result.isConfirmed) {
            setLoader(true);
            try {
                const formData = new FormData();
                formData.append("paragrah", paragrah);

                // append each item property directly so that files live inside array objects
                const itemsMetadata = items.map((item, index) => {
                    const metadata = {
                        title: item.title,
                        subtitle: item.subtitle,
                        paragraph: item.paragraph,
                        sequence: parseInt(item.sequence) || 0,
                        acronyms: item.acronyms,
                    };

                    if (item.id) {
                        metadata.id = item.id;
                    }

                    // Include existing URLs in metadata if not updating with a new file
                    if (typeof item.image === 'string' && item.image) {
                        metadata.image = item.image;
                    }
                    if (typeof item.banner === 'string' && item.banner) {
                        metadata.banner = item.banner;
                    }

                    // Append new files as top-level fields
                    if (item.image instanceof File) {
                        formData.append(`item_image_${index}`, item.image);
                    }
                    if (item.banner instanceof File) {
                        formData.append(`item_banner_${index}`, item.banner);
                    }

                    return metadata;
                });

                formData.append("array", JSON.stringify(itemsMetadata));

                const response = id
                    ? await patchRequest(`/api/user/industry-parents/${id}/`, formData, "multipart/form-data")
                    : await postRequest(`/api/user/industry-parents/`, formData, "multipart/form-data");

                if (response && response.status !== 500 && !response.data.error) {
                    successModal.fire({
                        text: `Buying Industry and Items ${id ? "Updated" : "Added"} Successfully`,
                        confirmButtonText: "Continue",
                    });
                    navigate("/scofa/buying-industry");
                } else {
                    throw new Error(response.data.message || "Server Error");
                }
            } catch (error) {
                console.error("Submission error:", error);
                errorModal.fire({
                    text: `Buying Industry Update Failed`,
                    confirmButtonText: "Continue",
                });
            } finally {
                setLoader(false);
            }
        }
    };

    const onSortEnd = (result) => {
        if (!result.destination) return;

        const oldIndex = result.source.index;
        const newIndex = result.destination.index;

        if (oldIndex === newIndex) return;

        const reorderedList = Array.from(items);
        const [movedItem] = reorderedList.splice(oldIndex, 1);
        reorderedList.splice(newIndex, 0, movedItem);

        const updatedItems = reorderedList.map((item, idx) => ({
            ...item,
            sequence: idx + 1 // Assign new sequence based on 1-indexed position
        }));
        setItems(updatedItems);
    };

    const handleSequenceUpdate = async () => {
        const result = await questionModal.fire({
            title: "Are you sure you want to update the sequences?",
            confirmButtonText: "Update Sequences",
        });

        if (result.isConfirmed) {
            setLoader(true);
            try {
                const sequenceData = items
                    .filter(item => item.id) // Only items with ID can be updated
                    .map(item => ({
                        id: item.id,
                        sequence: parseInt(item.sequence) || 0
                    }));

                const formData = new FormData();
                formData.append("array", JSON.stringify(sequenceData));

                const response = await patchRequest(`/api/user/industry-parents/${id}/`, formData, "multipart/form-data");

                if (response && response.status !== 500 && !response.data.error) {
                    successModal.fire({
                        text: "Sequences Updated Successfully",
                        confirmButtonText: "Continue",
                    });
                } else {
                    throw new Error(response.data.message || "Server Error");
                }
            } catch (error) {
                console.error("Sequence Update Error:", error);
                errorModal.fire({
                    text: `Sequence Update Failed`,
                    confirmButtonText: "Continue",
                });
            } finally {
                setLoader(false);
            }
        }
    };

    const tableHeaders = [
        { key: "sn", title: "S.No" },
        { key: "title", title: "Title" },
        { key: "subtitle", title: "Subtitle" },
        // { key: "sequence", title: "Sequence" },
        { key: "actions", title: "Actions" },
    ];

    if (loader) {
        return <CustomLoader />
    }

    return (
        <DashboardLayout>
            <div className="container-fluid">
                <div className="row mb-3">
                    <div className="col-9 mb-2">
                        <h2 className="mainTitle">
                            <BackButton />
                            {id ? `Edit ` : `Add `} Buying Industry
                        </h2>
                    </div>
                    <div className="col-3 text-end">
                        <CustomButton
                            variant="primaryButton"
                            text={id ? "Update Parent & Items" : "Add Parent & Items"}
                            onClick={handleSubmit}
                        />
                    </div>
                </div>
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="row mb-4">
                            <div className="col-12 mb-2">
                                <div className="inputWrapper">
                                    <label className="mainLabel">Buying Industry Paragraph*</label>
                                    <textarea
                                        placeholder="Enter Buying Industry Paragraph"
                                        className="mainInput"
                                        style={{ height: '100px' }}
                                        value={paragrah}
                                        onChange={(e) => setParagrah(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-12 d-flex justify-content-between align-items-center mb-3">
                                <h4 className="subTitle">Buying Industry Items</h4>
                                <div className="d-flex gap-2">
                                    {id && (
                                        <button type="button" className="customButton secondaryButton" onClick={handleSequenceUpdate}>
                                            Update Sequences
                                        </button>
                                    )}
                                    <button type="button" className="customButton primaryButton" onClick={() => handleOpenModal()}>
                                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Item
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-12">
                                <DragDropContext onDragEnd={onSortEnd}>
                                    <Droppable droppableId="industry-parent-items-list">
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                                <CustomTable headers={tableHeaders}>
                                                    <tbody>
                                                        {items.map((item, index) => (
                                                            <Draggable
                                                                draggableId={`item-${item.id || index}`}
                                                                index={index}
                                                                key={item.id || index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        className={snapshot.isDragging ? "dragging" : ""}
                                                                    >
                                                                        <td>{index + 1}</td>
                                                                        <td>{item.title}</td>
                                                                        <td>{item.subtitle}</td>
                                                                        {/* <td>
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FontAwesomeIcon icon={faBars} style={{ cursor: 'grab', color: '#888' }} />
                                                                                <span className="ms-2">{item.sequence}</span>
                                                                            </div>
                                                                        </td> */}
                                                                        <td>
                                                                            <Dropdown className="tableDropdown">
                                                                                <Dropdown.Toggle variant="transparent" className="notButton classicToggle">
                                                                                    <FontAwesomeIcon icon={faEllipsisV} />
                                                                                </Dropdown.Toggle>
                                                                                <Dropdown.Menu align="end" className="tableDropdownMenu">
                                                                                    <button className="dropdown-item" onClick={() => handleOpenModal(index)}>
                                                                                        <FontAwesomeIcon icon={faEdit} className="me-2" /> Edit
                                                                                    </button>
                                                                                    <button className="dropdown-item" onClick={() => handleRemoveItem(index)}>
                                                                                        <FontAwesomeIcon icon={faTrash} className="me-2" /> Delete
                                                                                    </button>
                                                                                </Dropdown.Menu>
                                                                            </Dropdown>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                        {items.length === 0 && (
                                                            <tr>
                                                                <td colSpan={tableHeaders.length} className="text-center">No items added yet.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </CustomTable>
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Item Modal */}
            <Modal show={showModal}
                dialogClassName='swap-modal-for-permission custom-modal'
                onHide={handleCloseModal} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title className='mx-2'>{editingIndex !== null ? "Edit" : "Add"} Industry Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <div className="inputWrapper">
                                    <label className="mainLabel">Title*</label>
                                    <input
                                        type="text"
                                        className="mainInput"
                                        value={currentItem.title}
                                        onChange={(e) => setCurrentItem({ ...currentItem, title: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 mb-3">
                                <div className="inputWrapper">
                                    <label className="mainLabel">Subtitle</label>
                                    <input
                                        type="text"
                                        className="mainInput"
                                        value={currentItem.subtitle}
                                        onChange={(e) => setCurrentItem({ ...currentItem, subtitle: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="col-md-12 mb-3">
                                <div className="inputWrapper">
                                    <label className="mainLabel">Content Paragraph*</label>
                                    <textarea
                                        className="mainInput"
                                        style={{ height: '120px' }}
                                        value={currentItem.paragraph}
                                        onChange={(e) => setCurrentItem({ ...currentItem, paragraph: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Acronyms Section */}
                            <div className="col-md-12 mb-3">
                                <div className="inputWrapper">
                                    <label className="mainLabel">Acronyms</label>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <input
                                            type="text"
                                            className="mainInput"
                                            placeholder="Add an acronym"
                                            value={newAcronym}
                                            onChange={(e) => setNewAcronym(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddAcronym()}
                                        />
                                        <button type="button" className="btn btn-primary btn-sm" onClick={handleAddAcronym} style={{ padding: '8px 15px' }}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {currentItem.acronyms.map((ac, idx) => (
                                            <div key={idx} className="statusBadge statusBadgeActive d-flex align-items-center gap-2" style={{ left: 0, padding: '0.4rem 1rem', borderRadius: '99px', fontSize: '13px', background: '#ffffff20', color: '#fff', border: '1px solid #ffffff30' }}>
                                                {ac}
                                                <FontAwesomeIcon
                                                    icon={faTimesCircle}
                                                    className="cursor-pointer"
                                                    style={{ fontSize: '14px', transition: '0.2s' }}
                                                    onClick={() => handleRemoveAcronym(idx)}
                                                    onMouseOver={(e) => e.currentTarget.style.color = '#ff4d4d'}
                                                    onMouseOut={(e) => e.currentTarget.style.color = '#fff'}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Image & Banner Section */}
                            {
                                currentItem.id ? (
                                    <EditImageComponent
                                        title="Main Image*"
                                        thumbnail={!(currentItem.image instanceof File) ? currentItem.imagePreview : null}
                                        handleImageChange={(e) => handleImageChange(e, 'image')}
                                        soundImage={currentItem.image instanceof File ? currentItem.imagePreview : null}
                                        handleRemoveImage={() => handleRemoveImage('image')}
                                    />
                                ) : (
                                    <AddImageComponent
                                        title="Main Image*"
                                        inputClass="col-md-6"
                                        soundImage={currentItem.imagePreview}
                                        handleImageChange={(e) => handleImageChange(e, 'image')}
                                        handleRemoveImage={() => handleRemoveImage('image')}
                                    />
                                )
                            }

                            {
                                currentItem.id ? (
                                    <EditImageComponent
                                        title="Banner Image*"
                                        thumbnail={!(currentItem.banner instanceof File) ? currentItem.bannerPreview : null}
                                        handleImageChange={(e) => handleImageChange(e, 'banner')}
                                        soundImage={currentItem.banner instanceof File ? currentItem.bannerPreview : null}
                                        handleRemoveImage={() => handleRemoveImage('banner')}
                                    />
                                ) : (
                                    <AddImageComponent
                                        title="Banner Image*"
                                        inputClass="col-md-6"
                                        soundImage={currentItem.bannerPreview}
                                        handleImageChange={(e) => handleImageChange(e, 'banner')}
                                        handleRemoveImage={() => handleRemoveImage('banner')}
                                    />
                                )
                            }
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <CustomButton variant="secondaryButton" text="Cancel" onClick={handleCloseModal} />
                    <CustomButton variant="primaryButton" text={editingIndex !== null ? "Update Item" : "Add Item"} onClick={handleSaveItem} />
                </Modal.Footer>
            </Modal>
        </DashboardLayout>
    );
};

export default IndustryParentAddEdit;
